import { signal, type Signal } from '@preact/signals';

export interface CacheEntry<T> {
  data: Signal<T | undefined>;
  loading: Signal<boolean>;
  error: Signal<Error | null>;
  updatedAt: number;
  refCount: number;
  inflight: Promise<T> | null;
  fetcher: (() => Promise<T>) | null;
  // Bumped on reset() so an in-flight fetch started under a previous tenant /
  // scope is discarded when it resolves instead of writing stale data into the cache.
  generation: number;
  gcTimer: ReturnType<typeof setTimeout> | null;
}

/**
 * Cuánto se conserva una entrada que ya no usa ninguna página. Volver a una
 * página dentro de esta ventana la pinta al momento con lo último que se vio
 * (y la refresca en segundo plano si pasó el `staleTime`), en vez de
 * recargarlo todo cada vez que se cambia de pestaña.
 */
export const GC_TIME_MS = 5 * 60_000;

const cache = new Map<string, CacheEntry<unknown>>();

export function getOrCreateEntry<T>(key: string): CacheEntry<T> {
  let entry = cache.get(key) as CacheEntry<T> | undefined;
  if (!entry) {
    entry = {
      data: signal<T | undefined>(undefined),
      loading: signal(false),
      error: signal<Error | null>(null),
      updatedAt: 0,
      refCount: 0,
      inflight: null,
      fetcher: null,
      generation: 0,
      gcTimer: null,
    };
    cache.set(key, entry as CacheEntry<unknown>);
  }
  return entry;
}

function matchesPrefix(key: string, keyOrPrefix: string): boolean {
  return (
    key === keyOrPrefix || key.startsWith(`${keyOrPrefix}:`) || key.startsWith(`${keyOrPrefix}.`)
  );
}

export function invalidate(keyOrPrefix: string): void {
  for (const [k, entry] of cache.entries()) {
    if (matchesPrefix(k, keyOrPrefix)) {
      if (entry.fetcher && entry.refCount > 0) {
        void runFetch(k, entry.fetcher);
      } else {
        entry.updatedAt = 0; // mark stale; next mount will refetch
      }
    }
  }
}

/**
 * Hard-reset cache entries matching the given key/prefix: clears stored data,
 * bumps the entry's generation so any in-flight fetch resolves to a no-op,
 * and triggers a fresh fetch when the entry is currently mounted. Use this
 * (not `invalidate`) when the underlying scope changed — e.g. on tenant
 * switch — so the UI doesn't flash data from the previous scope.
 */
export function reset(keyOrPrefix: string): void {
  for (const [k, entry] of cache.entries()) {
    if (matchesPrefix(k, keyOrPrefix)) {
      entry.generation += 1;
      entry.inflight = null;
      entry.data.value = undefined;
      entry.error.value = null;
      entry.updatedAt = 0;
      // Solo las montadas: las conservadas sin uso (ver GC_TIME_MS) se
      // recargan al volver a montarse.
      if (entry.fetcher && entry.refCount > 0) {
        void runFetch(k, entry.fetcher);
      }
    }
  }
}

/**
 * Recarga todo lo que hay en pantalla y marca lo demás como caducado. Para
 * cuando cambian los datos del servidor (fin de un sync, recálculo): las
 * pantallas abiertas se ponen al día sin pulsar "Actualizar datos".
 */
export function refreshMounted(): void {
  for (const [k, entry] of cache.entries()) {
    if (entry.fetcher && entry.refCount > 0) {
      void runFetch(k, entry.fetcher);
    } else {
      entry.updatedAt = 0;
    }
  }
}

export function clearCache(): void {
  for (const entry of cache.values()) {
    if (entry.gcTimer) clearTimeout(entry.gcTimer);
  }
  cache.clear();
}

/**
 * O(1) drop de UNA entrada cuando ya no está mounted (refCount=0) y no tiene
 * un fetch in-flight. Reemplaza el sweep completo que recorría todo el cache
 * en cada unmount (208+ entries → O(N²) en navegación rápida).
 *
 * Devuelve `true` si se borró la entrada, `false` si seguía en uso.
 */
export function dropEntryIfUnused(key: string): boolean {
  const entry = cache.get(key);
  if (!entry) return false;
  if (entry.refCount === 0 && !entry.inflight) {
    if (entry.gcTimer) clearTimeout(entry.gcTimer);
    cache.delete(key);
    return true;
  }
  return false;
}

/**
 * Llamado desde `useQuery` al desmontar: programa el drop pasado
 * `GC_TIME_MS`. Si la entrada vuelve a montarse, el drop no hace nada porque
 * `refCount` ya no es 0.
 */
export function scheduleDropIfUnused(key: string, delay = GC_TIME_MS): void {
  const entry = cache.get(key);
  if (!entry || entry.refCount > 0) return;
  if (entry.gcTimer) clearTimeout(entry.gcTimer);
  entry.gcTimer = setTimeout(() => {
    entry.gcTimer = null;
    // El fetch en vuelo se resolvió o sigue: si sigue, se reintenta después.
    if (!dropEntryIfUnused(key) && entry.refCount === 0) {
      scheduleDropIfUnused(key, delay);
    }
  }, delay);
}

async function runFetch<T>(key: string, fetcher: () => Promise<T>): Promise<T> {
  const entry = getOrCreateEntry<T>(key);
  if (entry.inflight) return entry.inflight;
  const startedAt = entry.generation;
  entry.loading.value = true;
  entry.error.value = null;
  const promise = fetcher().then(
    (data) => {
      // Discard the result if the entry was reset (e.g. tenant switch) while
      // this fetch was in flight — otherwise we'd overwrite the cleared signal
      // with stale data from the previous scope.
      if (entry.generation !== startedAt) return data;
      entry.data.value = data;
      entry.updatedAt = Date.now();
      entry.loading.value = false;
      entry.inflight = null;
      return data;
    },
    (err: unknown) => {
      if (entry.generation !== startedAt) {
        throw err;
      }
      entry.error.value = err instanceof Error ? err : new Error(String(err));
      entry.loading.value = false;
      entry.inflight = null;
      throw err;
    },
  );
  entry.inflight = promise;
  return promise;
}

export const queryRunner = { runFetch };
